import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  ArrowLeft, ArrowRight, Briefcase, CheckCircle, Circle, Clock,
  Paperclip, Upload, Download, Trash2, FileText, ExternalLink,
  Users, Calendar, Plus, Target, X, Sparkles, Image as ImageIcon,
  Presentation,
} from 'lucide-react';
import { Care365Presentation } from './Care365Presentation';

const C = {
  bg: '#f8fafc', text: '#0f172a', muted: '#475569', light: '#94a3b8',
  border: '#e2e8f0', purple: '#7c3aed', green: '#16a34a', amber: '#f59e0b',
  red: '#dc2626', blue: '#2563eb', purpleBg: '#f5f3ff', amberBg: '#fffbeb',
};

const API_URL = process.env.REACT_APP_BACKEND_URL;
const ALLOWED_EXTS = ['docx', 'doc', 'pdf', 'xlsx', 'xls', 'png', 'jpg', 'jpeg'];
const ALLOWED_ACCEPT = '.docx,.doc,.pdf,.xlsx,.xls,.png,.jpg,.jpeg';
const MAX_SIZE = 25 * 1024 * 1024;

const fmtSize = (b) => {
  if (!b && b !== 0) return '';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
};

const phaseColors = {
  done: { bg: C.green, fg: 'white', border: C.green, label: 'Completed' },
  active: { bg: '#fff7ed', fg: '#c2410c', border: '#fb923c', label: 'In Progress' },
  upcoming: { bg: '#f1f5f9', fg: '#64748b', border: '#cbd5e1', label: 'Upcoming' },
};

/* ───────── Phases Timeline ───────── */
const PhasesTimeline = ({ phases, onUpdate, canEdit, toast }) => {
  const cycleStatus = (idx) => {
    if (!canEdit) return;
    const order = ['upcoming', 'active', 'done'];
    const next = [...phases];
    next[idx] = { ...next[idx], status: order[(order.indexOf(next[idx].status) + 1) % order.length] };
    onUpdate(next);
    if (toast) toast.success(`${next[idx].name} → ${next[idx].status}`);
  };

  return (
    <div className="bg-white rounded-xl shadow-sm p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2"><Target size={16} style={{ color: C.purple }} /><h3 className="text-sm font-bold" style={{ color: C.text }}>Project Phases</h3></div>
        <span className="text-[10px]" style={{ color: C.light }}>{canEdit ? 'Click a phase to change status' : 'Read-only'}</span>
      </div>
      <div className="relative">
        <div className="absolute left-3 top-2 bottom-2 w-[2px]" style={{ background: C.border }} />
        {phases.map((p, idx) => {
          const c = phaseColors[p.status] || phaseColors.upcoming;
          return (
            <div key={p.id} className="relative pl-10 pb-3 last:pb-0" data-testid={`phase-${p.id}`}>
              <button onClick={() => cycleStatus(idx)} disabled={!canEdit} className="absolute left-0 top-0 w-6 h-6 rounded-full flex items-center justify-center transition-transform hover:scale-110 disabled:cursor-default disabled:hover:scale-100" style={{ background: c.bg, border: `2px solid ${c.border}` }}>
                {p.status === 'done' ? <CheckCircle size={14} style={{ color: 'white' }} /> : p.status === 'active' ? <Clock size={12} style={{ color: c.fg }} /> : <Circle size={12} style={{ color: c.fg }} />}
              </button>
              <div className="ml-2 p-2.5 rounded-lg" style={{ background: c.bg, border: `1px solid ${c.border}` }}>
                <div className="text-xs font-bold" style={{ color: c.fg }}>{p.name}</div>
                <div className="text-[10px] mt-0.5 font-semibold uppercase tracking-wider" style={{ color: c.fg, opacity: 0.7 }}>{c.label}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

/* ───────── Milestones (auto from tasks) ───────── */
const MilestonesList = ({ milestones, tasks, project, onUpdate, canEdit, toast }) => {
  const [editing, setEditing] = useState(null);
  const projectTasks = tasks.filter(t => t.project === project.key);
  const doneCount = milestones.filter(m => m.done).length;

  const toggleTask = (mIdx, taskId) => {
    const next = [...milestones];
    const ids = new Set(next[mIdx].task_ids || []);
    if (ids.has(taskId)) ids.delete(taskId); else ids.add(taskId);
    next[mIdx] = { ...next[mIdx], task_ids: [...ids] };
    onUpdate(next);
  };

  return (
    <div className="bg-white rounded-xl shadow-sm p-5">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2"><Briefcase size={16} style={{ color: C.green }} /><h3 className="text-sm font-bold" style={{ color: C.text }}>Milestones</h3></div>
        <span className="text-xs font-bold tabular-nums" style={{ color: C.green }}>{doneCount}/{milestones.length} complete</span>
      </div>
      <div className="h-2 rounded-full overflow-hidden mb-4" style={{ background: C.border }}>
        <div className="h-full rounded-full transition-all" style={{ width: `${(doneCount / milestones.length) * 100}%`, background: C.green }} />
      </div>
      <div className="space-y-2">
        {milestones.map((m, mIdx) => (
          <div key={m.id} className="rounded-lg p-2.5" style={{ background: m.done ? `${C.green}10` : C.bg, border: `1px solid ${m.done ? `${C.green}40` : C.border}` }}>
            <div className="flex items-center gap-2">
              {m.done ? <CheckCircle size={14} style={{ color: C.green }} /> : <Circle size={14} style={{ color: C.light }} />}
              <span className={`text-xs flex-1 ${m.done ? 'line-through' : 'font-semibold'}`} style={{ color: m.done ? C.muted : C.text }}>{m.title}</span>
              {(m.task_ids || []).length > 0 && (
                <span className="text-[10px] tabular-nums px-1.5 py-0.5 rounded" style={{ background: 'white', color: C.muted }}>
                  {Math.round(m.progress || 0)}% · {(m.task_ids || []).length} task(s)
                </span>
              )}
              {canEdit && (
                <button onClick={() => setEditing(editing === mIdx ? null : mIdx)} data-testid={`milestone-link-${m.id}`} className="text-[10px] font-semibold px-2 py-0.5 rounded" style={{ background: 'white', color: C.purple, border: `1px solid ${C.border}` }}>
                  Link tasks
                </button>
              )}
            </div>
            {editing === mIdx && (
              <div className="mt-2 pt-2 border-t" style={{ borderColor: C.border }}>
                <div className="text-[10px] font-bold mb-1.5" style={{ color: C.muted }}>Select Kanban tasks that complete this milestone:</div>
                {projectTasks.length === 0 ? (
                  <p className="text-[11px] italic" style={{ color: C.light }}>No tasks yet for this project. Add tasks below.</p>
                ) : (
                  <div className="space-y-1 max-h-40 overflow-y-auto">
                    {projectTasks.map(t => {
                      const checked = (m.task_ids || []).includes(t.id);
                      return (
                        <label key={t.id} className="flex items-center gap-2 text-[11px] p-1.5 rounded cursor-pointer hover:bg-white" style={{ color: C.text }}>
                          <input type="checkbox" checked={checked} onChange={() => toggleTask(mIdx, t.id)} />
                          <span className={`flex-1 truncate ${t.status === 'done' ? 'line-through' : ''}`}>{t.title}</span>
                          <span className="text-[9px] px-1.5 py-0.5 rounded" style={{ background: t.status === 'done' ? `${C.green}20` : t.status === 'in_progress' ? `${C.amber}20` : C.border, color: t.status === 'done' ? C.green : t.status === 'in_progress' ? C.amber : C.muted }}>{t.status}</span>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

/* ───────── Compact Kanban (filtered to project) ───────── */
const PartnershipKanban = ({ tasks, project, token, onRefreshTasks, canEdit, toast }) => {
  const [newTitle, setNewTitle] = useState('');
  const projectTasks = tasks.filter(t => t.project === project.key);
  const cols = [
    { key: 'todo', label: 'To Do', color: C.light },
    { key: 'in_progress', label: 'In Progress', color: C.amber },
    { key: 'done', label: 'Done', color: C.green },
  ];

  const addTask = async () => {
    if (!newTitle.trim()) return;
    try {
      const res = await fetch(`${API_URL}/api/sme-workspace/tasks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ title: newTitle.trim(), description: '', project: project.key, status: 'todo' }),
      });
      if (!res.ok) throw new Error('Failed to create task');
      setNewTitle('');
      onRefreshTasks();
      toast.success('Task added');
    } catch (e) { toast.error(e.message); }
  };

  const moveTask = async (task, newStatus) => {
    try {
      await fetch(`${API_URL}/api/sme-workspace/tasks/${task.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ...task, status: newStatus }),
      });
      onRefreshTasks();
    } catch (e) { toast.error(e.message); }
  };

  const removeTask = async (task) => {
    if (!window.confirm(`Delete "${task.title}"?`)) return;
    try {
      await fetch(`${API_URL}/api/sme-workspace/tasks/${task.id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      onRefreshTasks();
      toast.success('Task deleted');
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm p-5">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2"><Target size={16} style={{ color: C.blue }} /><h3 className="text-sm font-bold" style={{ color: C.text }}>Activities (Kanban)</h3></div>
        <span className="text-xs" style={{ color: C.muted }}>{projectTasks.length} tasks</span>
      </div>
      {canEdit && (
        <div className="flex gap-2 mb-4">
          <input value={newTitle} onChange={e => setNewTitle(e.target.value)} onKeyDown={e => e.key === 'Enter' && addTask()} placeholder="Add a task and press Enter…" className="flex-1 px-3 py-2 rounded-lg text-xs outline-none" style={{ background: C.bg, border: `1px solid ${C.border}`, color: C.text }} data-testid="kanban-add-input" />
          <button onClick={addTask} className="px-3 py-2 rounded-lg text-xs font-bold text-white" style={{ background: C.purple }}>Add</button>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {cols.map(col => (
          <div key={col.key} className="p-2 rounded-lg" style={{ background: C.bg, border: `1px solid ${C.border}` }}>
            <div className="flex items-center justify-between mb-2 px-1">
              <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: col.color }}>{col.label}</span>
              <span className="text-[10px] tabular-nums" style={{ color: C.muted }}>{projectTasks.filter(t => t.status === col.key).length}</span>
            </div>
            <div className="space-y-1.5 min-h-[60px]">
              {projectTasks.filter(t => t.status === col.key).map(t => (
                <div key={t.id} className="p-2 rounded-md bg-white shadow-sm group" style={{ borderLeft: `3px solid ${col.color}` }}>
                  <div className="text-xs font-semibold" style={{ color: C.text }}>{t.title}</div>
                  {canEdit && (
                    <div className="flex items-center gap-1 mt-1.5 opacity-60 group-hover:opacity-100 transition-opacity">
                      {cols.filter(c => c.key !== col.key).map(c => (
                        <button key={c.key} onClick={() => moveTask(t, c.key)} className="text-[9px] font-semibold px-1.5 py-0.5 rounded" style={{ background: C.bg, color: c.color, border: `1px solid ${C.border}` }}>→ {c.label}</button>
                      ))}
                      <button onClick={() => removeTask(t)} className="ml-auto p-0.5 rounded hover:bg-red-50"><Trash2 size={10} style={{ color: C.red }} /></button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ───────── Drag-drop Attachments ───────── */
const AttachmentsZone = ({ project, token, onRefresh, canEdit, toast }) => {
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);
  const attachments = project.attachments || [];

  const upload = async (files) => {
    setUploading(true);
    try {
      for (const file of files) {
        const ext = (file.name.split('.').pop() || '').toLowerCase();
        if (!ALLOWED_EXTS.includes(ext)) { toast.error(`${file.name}: type not allowed`); continue; }
        if (file.size > MAX_SIZE) { toast.error(`${file.name}: too large`); continue; }
        const fd = new FormData();
        fd.append('file', file);
        const res = await fetch(`${API_URL}/api/sme-workspace/partnership-projects/${project.key}/attachments`, {
          method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd,
        });
        if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || 'Upload failed');
        toast.success(`${file.name} uploaded`);
      }
      onRefresh();
    } catch (e) { toast.error(e.message); }
    finally { setUploading(false); }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (!canEdit) return;
    upload([...e.dataTransfer.files]);
  };

  const handleDownload = async (att) => {
    try {
      const res = await fetch(`${API_URL}/api/sme-workspace/partnership-projects/${project.key}/attachments/${att.id}/download`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('Download failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = att.filename; a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.message); }
  };

  const handleDelete = async (att) => {
    if (!window.confirm(`Delete "${att.filename}"?`)) return;
    try {
      const res = await fetch(`${API_URL}/api/sme-workspace/partnership-projects/${project.key}/attachments/${att.id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('Delete failed');
      onRefresh();
      toast.success('Attachment deleted');
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm p-5">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2"><Paperclip size={16} style={{ color: C.purple }} /><h3 className="text-sm font-bold" style={{ color: C.text }}>Files & Documents</h3></div>
        <span className="text-xs" style={{ color: C.muted }}>{attachments.length} file(s)</span>
      </div>
      {canEdit && (
        <div onDragOver={e => { e.preventDefault(); setDragOver(true); }} onDragLeave={() => setDragOver(false)} onDrop={handleDrop} onClick={() => inputRef.current?.click()} className="border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-all mb-3" style={{ borderColor: dragOver ? C.purple : C.border, background: dragOver ? C.purpleBg : C.bg }} data-testid="dropzone">
          <Upload size={18} style={{ color: dragOver ? C.purple : C.light }} className="mx-auto mb-1" />
          <p className="text-xs font-semibold" style={{ color: C.text }}>{uploading ? 'Uploading…' : 'Drag & drop or click to upload'}</p>
          <p className="text-[10px] mt-0.5" style={{ color: C.light }}>Word, PDF, Excel, PNG, JPG — max 25 MB each</p>
          <input ref={inputRef} type="file" accept={ALLOWED_ACCEPT} multiple className="hidden" onChange={e => { upload([...e.target.files]); e.target.value = ''; }} />
        </div>
      )}
      {attachments.length > 0 ? (
        <div className="space-y-1.5">
          {attachments.map(att => (
            <div key={att.id} className="flex items-center gap-2 p-2 rounded-lg" style={{ background: C.bg, border: `1px solid ${C.border}` }}>
              <span className="w-7 h-7 rounded flex items-center justify-center bg-white" style={{ border: `1px solid ${C.border}` }}>
                {['png','jpg','jpeg'].includes(att.ext) ? <ImageIcon size={13} style={{ color: C.purple }} /> : <FileText size={13} style={{ color: ['pdf'].includes(att.ext) ? C.red : ['doc','docx'].includes(att.ext) ? C.blue : C.green }} />}
              </span>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-semibold truncate" style={{ color: C.text }}>{att.filename}</div>
                <div className="text-[10px]" style={{ color: C.light }}>{fmtSize(att.size)} · {att.uploaded_by?.name || 'Unknown'} · {new Date(att.uploaded_at).toLocaleDateString()}</div>
              </div>
              <button onClick={() => handleDownload(att)} className="p-1.5 rounded hover:bg-white"><Download size={12} style={{ color: C.blue }} /></button>
              {canEdit && <button onClick={() => handleDelete(att)} className="p-1.5 rounded hover:bg-red-50"><Trash2 size={12} style={{ color: C.red }} /></button>}
            </div>
          ))}
        </div>
      ) : (
        <p className="text-[11px] italic text-center py-2" style={{ color: C.light }}>No files uploaded yet.</p>
      )}
    </div>
  );
};

/* ───────── Project Detail Page ───────── */
const ProjectPage = ({ project, tasks, token, onRefresh, onRefreshTasks, onBack, canEdit, toast }) => {
  const [editingDesc, setEditingDesc] = useState(false);
  const [desc, setDesc] = useState(project.description || '');
  const [showPresentation, setShowPresentation] = useState(false);

  const isCare365 = project.key === 'care365_hfz' || (project.name || '').toLowerCase().includes('care365');

  useEffect(() => { setDesc(project.description || ''); }, [project.description]);

  const updateProject = async (patch) => {
    try {
      const res = await fetch(`${API_URL}/api/sme-workspace/partnership-projects/${project.key}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(patch),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || 'Update failed');
      onRefresh();
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div className="space-y-4">
      {showPresentation && <Care365Presentation onClose={() => setShowPresentation(false)} />}
      <button onClick={onBack} className="text-xs font-semibold flex items-center gap-1" style={{ color: C.muted }}>
        <ArrowLeft size={12} /> Back to Partnerships
      </button>

      {/* Hero */}
      <div className="rounded-2xl p-6 text-white relative overflow-hidden" style={{ background: `linear-gradient(135deg, ${project.color || C.purple} 0%, #0a5c2e 100%)` }}>
        <div className="absolute -right-12 -top-12 w-48 h-48 rounded-full opacity-15 bg-white" />
        <div className="relative z-10 flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0 flex-1">
            <div className="inline-flex items-center gap-1.5 text-[10px] font-bold px-2 py-0.5 rounded-full mb-2" style={{ background: 'rgba(255,255,255,0.2)' }}><Sparkles size={10} /> Partnership Project</div>
            <h1 className="text-2xl font-bold mb-1">{project.name}</h1>
            <p className="text-xs opacity-90">{project.tagline}</p>
            {project.meeting_date && <p className="text-[10px] mt-2 opacity-75 flex items-center gap-1"><Calendar size={10} /> Kick-off: {new Date(project.meeting_date).toLocaleDateString()}</p>}
          </div>
          {isCare365 && (
            <button
              onClick={() => setShowPresentation(true)}
              data-testid="open-care365-presentation-btn"
              className="flex-shrink-0 inline-flex items-center gap-2 text-xs font-bold px-4 py-2.5 rounded-xl transition-transform hover:scale-105 shadow-lg"
              style={{ background: 'white', color: '#0f766e' }}
            >
              <Presentation size={15} /> Visa presentation
            </button>
          )}
        </div>
        {project.hero_images && project.hero_images.length > 0 && (
          <div className="relative z-10 mt-4 grid grid-cols-2 md:grid-cols-4 gap-2">
            {project.hero_images.slice(0, 4).map((src, i) => (
              <div key={i} className="rounded-lg overflow-hidden bg-white/10" style={{ aspectRatio: '9 / 16' }}>
                <img src={src} alt="" className="w-full h-full object-cover" />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Participants */}
      {project.participants?.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm p-5">
          <div className="flex items-center gap-2 mb-2"><Users size={14} style={{ color: C.purple }} /><h3 className="text-sm font-bold" style={{ color: C.text }}>Participants</h3></div>
          <div className="flex flex-wrap gap-2">
            {project.participants.map((p, i) => (
              <span key={i} className="text-[11px] font-medium px-2.5 py-1 rounded-full" style={{ background: C.purpleBg, color: C.purple, border: `1px solid ${C.purple}30` }}>{p}</span>
            ))}
          </div>
        </div>
      )}

      {/* Description */}
      <div className="bg-white rounded-xl shadow-sm p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-bold" style={{ color: C.text }}>Project Overview</h3>
          {canEdit && (
            <button onClick={() => editingDesc ? (updateProject({ description: desc }), setEditingDesc(false)) : setEditingDesc(true)} className="text-[10px] font-semibold px-2.5 py-1 rounded-lg" style={{ background: C.purpleBg, color: C.purple }} data-testid="edit-desc-btn">
              {editingDesc ? 'Save' : 'Edit'}
            </button>
          )}
        </div>
        {editingDesc ? (
          <textarea value={desc} onChange={e => setDesc(e.target.value)} rows={10} className="w-full px-3 py-2 rounded-lg text-xs font-mono" style={{ background: C.bg, border: `1px solid ${C.border}`, color: C.text }} />
        ) : (
          <div className="prose prose-sm max-w-none text-xs leading-relaxed" style={{ color: C.text }} dangerouslySetInnerHTML={{ __html: project.description || '<em>No description yet.</em>' }} />
        )}
      </div>

      {/* Two-column on desktop */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <PhasesTimeline phases={project.phases || []} onUpdate={(phases) => updateProject({ phases })} canEdit={canEdit} toast={toast} />
        <MilestonesList milestones={project.milestones || []} tasks={tasks} project={project} onUpdate={(milestones) => updateProject({ milestones })} canEdit={canEdit} toast={toast} />
      </div>

      <PartnershipKanban tasks={tasks} project={project} token={token} onRefreshTasks={onRefreshTasks} canEdit={canEdit} toast={toast} />
      <AttachmentsZone project={project} token={token} onRefresh={onRefresh} canEdit={canEdit} toast={toast} />

      {project.links?.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm p-5">
          <div className="flex items-center gap-2 mb-2"><ExternalLink size={14} style={{ color: C.blue }} /><h3 className="text-sm font-bold" style={{ color: C.text }}>Related Links</h3></div>
          <ul className="space-y-1">
            {project.links.map((l, i) => (
              <li key={i}><a href={l.url} target="_blank" rel="noopener noreferrer" className="text-xs font-semibold underline" style={{ color: C.blue }}>{l.label}</a></li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

/* ───────── Main Partnerships View ───────── */
export const PartnershipsView = ({ token, user, toast }) => {
  const [projects, setProjects] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [selectedKey, setSelectedKey] = useState(null);
  const [loading, setLoading] = useState(true);

  const canEdit = ['voxit', 'ministry', 'hfz'].includes(user.role);

  const loadProjects = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/sme-workspace/partnership-projects`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('Failed to load projects');
      setProjects(await res.json());
    } catch (e) { toast.error(e.message); }
  }, [token, toast]);

  const loadTasks = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/sme-workspace/tasks`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) return;
      setTasks(await res.json());
    } catch (e) { /* ignore */ }
  }, [token]);

  useEffect(() => {
    setLoading(true);
    Promise.all([loadProjects(), loadTasks()]).finally(() => setLoading(false));
  }, [loadProjects, loadTasks]);

  if (loading) return <div className="p-8 text-center text-sm" style={{ color: C.muted }}>Loading partnerships…</div>;

  const selected = projects.find(p => p.key === selectedKey);
  if (selected) {
    return <ProjectPage project={selected} tasks={tasks} token={token} onRefresh={loadProjects} onRefreshTasks={loadTasks} onBack={() => setSelectedKey(null)} canEdit={canEdit} toast={toast} />;
  }

  return (
    <div data-testid="partnerships-view">
      <div className="mb-5">
        <h2 className="text-xl font-bold mb-1" style={{ color: C.text }}>Partnership Projects</h2>
        <p className="text-xs" style={{ color: C.muted }}>Active collaboration projects between VoxIT and external partners.</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {projects.map(p => {
          const doneM = (p.milestones || []).filter(m => m.done).length;
          const doneP = (p.phases || []).filter(ph => ph.status === 'done').length;
          return (
            <motion.button key={p.key} whileHover={{ y: -2 }} onClick={() => setSelectedKey(p.key)} data-testid={`project-card-${p.key}`} className="text-left p-5 rounded-2xl bg-white shadow-sm transition-all hover:shadow-md overflow-hidden relative">
              <div className="absolute -right-8 -top-8 w-32 h-32 rounded-full opacity-10" style={{ background: p.color }} />
              <div className="relative z-10">
                <div className="inline-flex items-center gap-1.5 text-[10px] font-bold px-2 py-0.5 rounded-full mb-2" style={{ background: `${p.color}15`, color: p.color }}>
                  <Briefcase size={10} /> Partnership
                </div>
                <h3 className="text-lg font-bold mb-1" style={{ color: C.text }}>{p.name}</h3>
                <p className="text-xs mb-4" style={{ color: C.muted }}>{p.tagline}</p>
                <div className="flex items-center gap-3 text-[11px]" style={{ color: C.muted }}>
                  <span><strong style={{ color: C.text }}>{doneP}/{(p.phases || []).length}</strong> phases</span>
                  <span><strong style={{ color: C.text }}>{doneM}/{(p.milestones || []).length}</strong> milestones</span>
                  <span><strong style={{ color: C.text }}>{(p.attachments || []).length}</strong> files</span>
                </div>
                <div className="mt-3 inline-flex items-center gap-1 text-xs font-bold" style={{ color: p.color }}>
                  Open project <ArrowRight size={12} />
                </div>
              </div>
            </motion.button>
          );
        })}
      </div>
    </div>
  );
};
